import React, { useState } from 'react';

interface FormState {
  name: string;
  company: string;
  email: string;
  role: string;
  framework: string;
  message: string;
}

interface FormErrors {
  name?: string;
  company?: string;
  email?: string;
  message?: string;
}

const initialForm: FormState = {
  name: '',
  company: '',
  email: '',
  role: '',
  framework: '',
  message: '',
};

const frameworks = [
  'BRSR (India)',
  'CSRD / ESRS (Europe)',
  'EU Taxonomy',
  'CBAM',
  'Product Carbon Footprint',
  'Corporate Carbon Footprint',
  'Sustainable Procurement',
  'Multiple / unsure',
];

function validate(form: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.name.trim()) errors.name = 'Name is required.';
  if (!form.company.trim()) errors.company = 'Company is required.';
  if (!form.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = 'Please enter a valid email address.';
  }
  if (!form.message.trim()) errors.message = 'Please tell us a bit about your situation.';
  return errors;
}

export default function Contact(): React.ReactElement {
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitted(true);
  }

  return (
    <main>
      <div className="page-hero">
        <div className="container">
          <span className="eyebrow" style={{ color: '#67E8F9', justifyContent: 'center', display: 'flex' }}>Contact</span>
          <h1>Book a demo or talk to us about your framework.</h1>
          <p>
            Tell us which regulatory obligations you are working with and we will configure a
            demonstration around your specific situation. Most clients are collecting structured
            data within two weeks of onboarding.
          </p>
        </div>
      </div>

      <section className="section-pad" style={{ background: 'var(--white)' }}>
        <div className="container">
          <div className="contact-grid">
            <div className="contact-form-col">
              {submitted ? (
                <div style={{ background: 'var(--emerald)', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
                  <span style={{ fontSize: '2rem', display: 'block', marginBottom: '16px' }}>&#10003;</span>
                  <h3 style={{ color: 'var(--white)', marginBottom: '12px' }}>Message received.</h3>
                  <p style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.65 }}>
                    We will be in touch within one business day to arrange a call or demonstration
                    tailored to your regulatory obligations.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label" htmlFor="name">Full name *</label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        className={`form-input${errors.name ? ' form-input-error' : ''}`}
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        autoComplete="name"
                      />
                      {errors.name && <span className="form-error">{errors.name}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="company">Company *</label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        className={`form-input${errors.company ? ' form-input-error' : ''}`}
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Your organisation"
                        autoComplete="organization"
                      />
                      {errors.company && <span className="form-error">{errors.company}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label" htmlFor="email">Work email *</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        className={`form-input${errors.email ? ' form-input-error' : ''}`}
                        value={form.email}
                        onChange={handleChange}
                        placeholder="you@company.com"
                        autoComplete="email"
                      />
                      {errors.email && <span className="form-error">{errors.email}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="role">Your role</label>
                      <input
                        id="role"
                        name="role"
                        type="text"
                        className="form-input"
                        value={form.role}
                        onChange={handleChange}
                        placeholder="e.g. Head of Sustainability"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="framework">Primary framework / obligation</label>
                    <select
                      id="framework"
                      name="framework"
                      className="form-input form-select"
                      value={form.framework}
                      onChange={handleChange}
                    >
                      <option value="">Select a framework&hellip;</option>
                      {frameworks.map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="message">Tell us about your situation *</label>
                    <textarea
                      id="message"
                      name="message"
                      className={`form-input form-textarea${errors.message ? ' form-input-error' : ''}`}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Which frameworks are you working with? What is your reporting timeline? What challenges are you facing?"
                      rows={5}
                    />
                    {errors.message && <span className="form-error">{errors.message}</span>}
                  </div>

                  <button type="submit" className="btn btn-primary" style={{ alignSelf: 'flex-start', minWidth: '180px' }}>
                    Send message
                  </button>
                </form>
              )}
            </div>

            <div className="contact-info-col">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                <div>
                  <h3 style={{ color: 'var(--navy)', marginBottom: '12px', fontSize: '1.125rem' }}>Get in touch</h3>
                  <p style={{ color: 'var(--slate-500)', lineHeight: 1.65, marginBottom: '12px' }}>
                    We work with enterprises to configure Actula around their specific regulatory
                    obligations, entity structure, and reporting timeline.
                  </p>
                  <a href="mailto:hello@esgtriage.com" style={{ color: 'var(--teal)', textDecoration: 'none', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>&#9993;</span> hello@esgtriage.com
                  </a>
                </div>

                <div style={{ borderTop: '1px solid var(--slate-200)', paddingTop: '28px' }}>
                  <h4 style={{ color: 'var(--navy)', marginBottom: '16px', fontSize: '1rem' }}>What happens next</h4>
                  <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {[
                      'We review your message and identify which modules and frameworks are most relevant.',
                      'We reach out within one business day to arrange a call.',
                      'We run a demonstration configured for your specific obligations and entity structure.',
                      'We scope an onboarding plan. Most clients are collecting structured data within two weeks.',
                    ].map((step, i) => (
                      <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <span style={{ minWidth: '24px', height: '24px', background: 'var(--teal)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, marginTop: '2px' }}>{i + 1}</span>
                        <p style={{ color: 'var(--slate-500)', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>{step}</p>
                      </li>
                    ))}
                  </ol>
                </div>

                <div style={{ background: 'var(--slate-50)', borderRadius: '12px', padding: '24px', borderTop: '3px solid var(--teal)' }}>
                  <h4 style={{ color: 'var(--navy)', marginBottom: '12px', fontSize: '0.9375rem' }}>Enterprise-ready</h4>
                  {[
                    'Enterprise-grade security',
                    'GDPR compliant &middot; EU data residency',
                    'Audit-ready from day one',
                    'Role-based access control',
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: i < 3 ? '8px' : 0 }}>
                      <span style={{ color: 'var(--emerald)', fontWeight: 700 }}>&#10003;</span>
                      <span style={{ color: 'var(--slate-500)', fontSize: '0.875rem' }} dangerouslySetInnerHTML={{ __html: item }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
